import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import {
  Music, Piano, Mic, GraduationCap, Calendar, Sparkles,
  Gift, Dumbbell, Factory, ChevronRight, ChevronDown, Users, Play,
  Star, CheckCircle2, Clock, Video, Headphones, Zap, UserPlus, RefreshCw, ArrowRight,
  Search, MessageSquare, Package, BookOpen, Globe, Award
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1551084219-d8cab9d7f776?crop=entropy&cs=srgb&fm=jpg&q=85";

const classroomIcons = {
  'music-gym': Dumbbell,
  'music-factory': Factory,
  'modular-piano': Piano,
  'theory-ear-training': Mic,
  'seasonal-genre': Calendar,
  'new-offerings': Sparkles,
  'foundation-lessons': GraduationCap,
  'perks-tiers': Gift,
};

const perksData = [
  {
    title: 'Productivity Tools',
    description: 'Curated tools and resources to help you practice smarter, not just harder.',
    icon: Zap
  },
  {
    title: 'Practice Apps',
    description: 'Access to the best practice apps with guidance on how to actually use them.',
    icon: Headphones
  },
  {
    title: 'Doubt Clearing',
    description: 'Stuck on something? Get your questions answered without waiting.',
    icon: Users
  },
  {
    title: 'In-Person Studio',
    description: 'Train at our Bangalore studio with professional recording equipment.',
    icon: Video
  }
];

export const LandingPage = () => {
  const [classrooms, setClassrooms] = useState([]);
  const [expandedClassroom, setExpandedClassroom] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  useEffect(() => {
    fetchClassrooms();
  }, []);

  const fetchClassrooms = async () => {
    try {
      const response = await axios.get(`${API}/classrooms`);
      setClassrooms(response.data.filter(c => c.id !== 'perks-tiers'));
    } catch (error) {
      console.error('Error fetching classrooms:', error);
    }
  };

  const toggleClassroom = (classroomId) => {
    setExpandedClassroom(expandedClassroom === classroomId ? null : classroomId);
  };

  const toggleDescription = (classId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <p className="font-accent text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-6 animate-fade-in-up">
            Piano Education, Redefined
          </p>
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-[#EDEDED] mb-6 animate-fade-in-up animate-delay-100">
            Learn Piano
            <span className="block text-gradient-gold">Your Way</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-[#A1A1AA] max-w-2xl mx-auto mb-10 animate-fade-in-up animate-delay-200">
            Whether you want to play your favorite songs, accompany yourself while singing,
            jam with a band, or dive deep into jazz and gospel — we've built a system that
            lets you pick exactly what you want to learn.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animate-delay-300">
            <Link to="/apply">
              <Button className="btn-gold rounded-full px-10 py-5 text-lg font-semibold">
                Build Your Semester
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
            <Link to="/track">
              <Button variant="outline" className="rounded-full px-8 py-5 text-lg border-white/20 text-[#EDEDED] hover:bg-white/10">
                <Search size={18} className="mr-2" />
                Track Application
              </Button>
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-[#D4AF37] flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-[#D4AF37] rounded-full" />
          </div>
        </div>
      </section>

      {/* 3 Forms Section - Who Are You? */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-accent text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">
              Get Started
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#EDEDED] mb-4">
              Which One Are You?
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Choose the path that fits you best. Each form helps us understand your needs so we can build your perfect semester.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* New Student */}
            <Link to="/apply" className="block group" data-testid="form-new-student">
              <div className="glass-card rounded-2xl p-8 h-full hover-lift border-2 border-transparent hover:border-emerald-500/50 transition-all">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <UserPlus className="text-emerald-400" size={32} />
                </div>
                <h3 className="font-heading text-2xl text-[#F5F5DC] mb-3">New Student</h3>
                <p className="text-[#A1A1AA] mb-6">
                  Never learned with us before? Start here. Tell us about your musical background, 
                  what you want to achieve, and we'll craft your first semester together.
                </p>
                <div className="flex items-center text-emerald-400 font-medium">
                  Start Application
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Existing Student */}
            <Link to="/repackage" className="block group" data-testid="form-existing-student">
              <div className="glass-card rounded-2xl p-8 h-full hover-lift border-2 border-transparent hover:border-blue-500/50 transition-all">
                <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <RefreshCw className="text-blue-400" size={32} />
                </div>
                <h3 className="font-heading text-2xl text-[#F5F5DC] mb-3">Current Student</h3>
                <p className="text-[#A1A1AA] mb-6">
                  Already in the middle of a semester? We've revamped our syllabus with new classes, 
                  electives, and jam sessions. See what's new and customize your plan.
                </p>
                <div className="flex items-center text-blue-400 font-medium">
                  Explore New Options
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Returning Student */}
            <Link to="/returning" className="block group" data-testid="form-returning-student">
              <div className="glass-card rounded-2xl p-8 h-full hover-lift border-2 border-transparent hover:border-purple-500/50 transition-all">
                <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Star className="text-purple-400" size={32} />
                </div>
                <h3 className="font-heading text-2xl text-[#F5F5DC] mb-3">Returning Student</h3>
                <p className="text-[#A1A1AA] mb-6">
                  Completed semesters before and ready to come back? A lot has changed! 
                  Tell us what worked, what you want to explore, and let's plan your return.
                </p>
                <div className="flex items-center text-purple-400 font-medium">
                  Welcome Back
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* What's Different Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="font-accent text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">
              How We Teach
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#EDEDED] mb-6">
              Not Your Typical Piano Lessons
            </h2>
            <p className="text-[#A1A1AA] text-lg leading-relaxed">
              Forget rigid syllabi. We've built a modular system where you pick what you want 
              to learn—from transcription and improvisation to gospel piano and Coke Studio arrangements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="glass-card rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="text-[#D4AF37]" size={28} />
              </div>
              <h3 className="font-heading text-xl text-[#F5F5DC] mb-2">Pick Your Classes</h3>
              <p className="text-[#A1A1AA] text-sm">
                60+ classes across 7 categories. Take what you need, skip what you don't.
              </p>
            </div>
            
            <div className="glass-card rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <Video className="text-[#D4AF37]" size={28} />
              </div>
              <h3 className="font-heading text-xl text-[#F5F5DC] mb-2">HD Recordings</h3>
              <p className="text-[#A1A1AA] text-sm">
                Can't make a live session? Every class is recorded with study materials.
              </p>
            </div>
            
            <div className="glass-card rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <Users className="text-[#D4AF37]" size={28} />
              </div>
              <h3 className="font-heading text-xl text-[#F5F5DC] mb-2">Jam Sessions</h3>
              <p className="text-[#A1A1AA] text-sm">
                Play with other musicians. Learn to listen, respond, and create together.
              </p>
            </div>
            
            <div className="glass-card rounded-2xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <Play className="text-[#D4AF37]" size={28} />
              </div>
              <h3 className="font-heading text-xl text-[#F5F5DC] mb-2">Studio Access</h3>
              <p className="text-[#A1A1AA] text-sm">
                In-person workshops at our Bangalore studio. Real equipment, real experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-accent text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">
              Simple Process
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#EDEDED] mb-4">
              How It Works
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              From your first click to your first lesson — here's what happens.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Tell Us About You',
                description: 'Fill out a short form about your musical background, goals, and the classes that interest you.',
                icon: MessageSquare,
              },
              {
                step: '02',
                title: 'We Design Your Semester',
                description: 'Our team reviews your submission and crafts a personalized package with the right classes, hours, and schedule.',
                icon: Package,
              },
              {
                step: '03',
                title: 'Review & Enroll',
                description: 'We send you a detailed semester plan. Review it, ask questions, tweak if needed — then lock it in.',
                icon: BookOpen,
              },
              {
                step: '04',
                title: 'Start Learning',
                description: 'Attend live sessions, access HD recordings, join jam sessions, and grow as a musician — your way.',
                icon: Play,
              },
            ].map((item) => (
              <div key={item.step} className="text-center relative">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="text-[#D4AF37]" size={28} />
                </div>
                <span className="font-accent text-[#D4AF37] text-xs tracking-widest">{item.step}</span>
                <h3 className="font-heading text-xl text-[#F5F5DC] mt-2 mb-3">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why NSM Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-accent text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">
              Why Nathaniel School
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#EDEDED] mb-4">
              Built for Real Musicians
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Most piano courses teach you a fixed syllabus. We let you design your own — with guidance from professionals who've played on stages and studios worldwide.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="glass-card rounded-2xl p-8 hover-lift">
              <Globe className="text-[#D4AF37] mb-4" size={32} />
              <h3 className="font-heading text-xl text-[#F5F5DC] mb-3">Learn from Anywhere</h3>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">
                Students from India, the US, UK, UAE, and beyond. All live sessions are online with HD recordings available 24/7. Join from your living room.
              </p>
            </div>
            <div className="glass-card rounded-2xl p-8 hover-lift">
              <Award className="text-[#D4AF37] mb-4" size={32} />
              <h3 className="font-heading text-xl text-[#F5F5DC] mb-3">Professional Faculty</h3>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">
                Led by Jason Zac — a pianist, producer, and educator with decades of experience in gospel, jazz, Bollywood, and contemporary music.
              </p>
            </div>
            <div className="glass-card rounded-2xl p-8 hover-lift">
              <Star className="text-[#D4AF37] mb-4" size={32} />
              <h3 className="font-heading text-xl text-[#F5F5DC] mb-3">60+ Classes, Your Pick</h3>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">
                Improvisation, transcription, Coke Studio arrangements, gospel piano, Latin grooves, production — choose what matters to you. Skip the rest.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Classrooms Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-accent text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">
              What You Can Learn
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#EDEDED] mb-4">
              7 Ways to Grow
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Each classroom focuses on a different aspect of your playing. 
              Click any to see what's inside.
            </p>
          </div>

          <div className="space-y-4">
            {classrooms.map((classroom) => {
              const IconComponent = classroomIcons[classroom.id] || Music;
              const isExpanded = expandedClassroom === classroom.id;
              
              return (
                <div
                  key={classroom.id}
                  className="glass-card rounded-2xl overflow-hidden"
                  data-testid={`classroom-card-${classroom.id}`}
                >
                  <button
                    onClick={() => toggleClassroom(classroom.id)}
                    className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                        <IconComponent className="text-[#D4AF37]" size={28} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-heading text-xl text-[#F5F5DC]">{classroom.name}</h3>
                        <p className="text-[#A1A1AA] text-sm">{classroom.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[#D4AF37] text-sm hidden md:block">
                        {classroom.classes?.length || 0} classes
                      </span>
                      {isExpanded ? (
                        <ChevronDown className="text-[#D4AF37]" size={24} />
                      ) : (
                        <ChevronRight className="text-[#A1A1AA]" size={24} />
                      )}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="border-t border-white/10 p-6 bg-black/30">
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {classroom.classes?.map((cls) => {
                          const isDescriptionExpanded = expandedDescriptions[cls.id];
                          const shouldTruncate = cls.description.length > 120;

                          return (
                            <div
                              key={cls.id}
                              className="p-4 rounded-lg border border-white/10 hover:border-[#D4AF37]/30 transition-colors"
                            >
                              <h4 className="text-[#F5F5DC] font-medium mb-2">{cls.name}</h4>
                              <p className="text-[#A1A1AA] text-sm mb-2">
                                {shouldTruncate && !isDescriptionExpanded
                                  ? `${cls.description.substring(0, 120)}...`
                                  : cls.description}
                              </p>
                              {shouldTruncate && (
                                <button
                                  onClick={() => toggleDescription(cls.id)}
                                  className="text-[#D4AF37] text-xs hover:underline mb-3 flex items-center gap-1"
                                >
                                  {isDescriptionExpanded ? (
                                    <>Show less <ChevronDown size={12} /></>
                                  ) : (
                                    <>Read more <ChevronRight size={12} /></>
                                  )}
                                </button>
                              )}
                              <div className="flex items-center gap-3 text-xs text-[#A1A1AA] mt-3">
                                <span className="flex items-center gap-1">
                                  <Clock size={12} className="text-[#D4AF37]" />
                                  {cls.duration_minutes || 60} min
                                </span>
                                {cls.delivery_mode === 'both' && (
                                  <span className="flex items-center gap-1">
                                    <Video size={12} className="text-[#D4AF37]" />
                                    Live + Recording
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Perks Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-accent text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">
              Beyond The Lessons
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#EDEDED] mb-4">
              What Else You Get
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Learning music isn't just about your weekly lesson. Here's what supports you between sessions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {perksData.map((perk, index) => (
              <div 
                key={index}
                className="glass-card rounded-2xl p-6 text-center hover-lift"
              >
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  <perk.icon className="text-[#D4AF37]" size={24} />
                </div>
                <h3 className="font-heading text-lg text-[#F5F5DC] mb-2">{perk.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{perk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#EDEDED] mb-6">
            Ready to Start?
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-10 max-w-2xl mx-auto">
            Pick the form that matches where you are, and let's build your musical journey together.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/apply">
              <Button className="btn-gold rounded-full px-8 py-4 text-lg">
                New Student
              </Button>
            </Link>
            <Link to="/repackage">
              <Button variant="outline" className="rounded-full px-8 py-4 text-lg border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black">
                Current Student
              </Button>
            </Link>
            <Link to="/returning">
              <Button variant="outline" className="rounded-full px-8 py-4 text-lg border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-black">
                Returning Student
              </Button>
            </Link>
          </div>

          <div className="mt-8">
            <Link to="/track" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm inline-flex items-center gap-2">
              <Search size={14} />
              Already applied? Track your application status
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
